/* ek-market-selector.js
   Global market selector logic supporting multiple instances on a page.
   - Defines custom element <localization-form> once
   - Handles open/close behavior scoped per instance
   - Submits PUT /localization with selected country/language
*/

(() => {
  if (typeof window === 'undefined') return;

  const defineOnce = (name, ctor) => {
    try {
      if (!customElements.get(name)) customElements.define(name, ctor);
    } catch (_) {}
  };

  const emit = (name, detail) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (_) {}
  };

  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
        button: this.querySelector('button[data-ek-loc-btn]') || this.querySelector('button'),
        panel: this.querySelector('ul[data-ek-loc-panel]') || this.querySelector('ul'),
      };

      if (!this.elements.button || !this.elements.panel) return;

      this.onButtonClick = this.onButtonClick.bind(this);
      this.onButtonBlur = this.onButtonBlur.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onItemClick = this.onItemClick.bind(this);

      this.elements.button.addEventListener('click', this.onButtonClick);
      this.elements.button.addEventListener('focusout', this.onButtonBlur);
      this.addEventListener('keyup', this.onKeyUp);
      this.querySelectorAll('a[data-value], a').forEach((item) =>
        item.addEventListener('click', this.onItemClick)
      );
    }

    hidePanel() {
      this.elements.button.setAttribute('aria-expanded', 'false');
      this.elements.panel.setAttribute('hidden', '');
    }

    onKeyUp(event) {
      if ((event.code || '').toUpperCase() !== 'ESCAPE') return;
      this.hidePanel();
      this.elements.button.focus();
    }

    onItemClick(event) {
      event.preventDefault();
      const link = event.currentTarget;
      const form = this.querySelector('form');
      const value = (link && link.dataset && link.dataset.value) || '';
      if (this.elements.input) this.elements.input.value = value;

      // Notify listeners of impending market change (before submit)
      emit('ek:market-change', { country: value || undefined, language: undefined, source: 'manual' });

      if (form) form.submit();
    }

    onButtonClick() {
      const expanded = this.elements.button.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        this.hidePanel();
      } else {
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
      }
    }

    onButtonBlur(event) {
      const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
      if (event.relatedTarget === null || shouldClose) this.hidePanel();
    }
  }

  defineOnce('localization-form', LocalizationForm);

  // Announce the current market as soon as possible
  try {
    const country = window.Shopify && window.Shopify.country;
    const language = window.Shopify && window.Shopify.language;
    if (country || language) {
      // Defer to ensure listeners can attach first
      setTimeout(() => emit('ek:market-ready', { country, language }), 0);
    }
  } catch (_) {}

})();
